/**
 * The page and text layers are whole-page canvases (a long site is ~1100x5000 px each) that the
 * game edits every frame while things blow up. Drawing such a canvas straight onto the screen makes
 * the browser snapshot and re-upload the entire page whenever it changed, even though only the
 * view is shown and only a crater's worth of pixels moved. On phones that was the long hitches.
 *
 * LayerCache keeps GPU-side tiles of a layer, tracks which tiles writes touch, and re-uploads only
 * those tiles (and only once they are on screen). copy() reads a region without snapshotting the
 * whole layer, for the code that crops pieces out of the page.
 */
const T = 256;

export class LayerCache {
  readonly cols: number;
  readonly rows: number;
  private tiles: Array<{ c: HTMLCanvasElement; g: CanvasRenderingContext2D } | null>;
  private dirty: Uint8Array;
  private scratch: HTMLCanvasElement | null = null;
  private sg: CanvasRenderingContext2D | null = null;

  constructor(readonly src: HTMLCanvasElement, readonly g: CanvasRenderingContext2D) {
    this.cols = Math.ceil(src.width / T);
    this.rows = Math.ceil(src.height / T);
    this.tiles = new Array(this.cols * this.rows).fill(null);
    this.dirty = new Uint8Array(this.cols * this.rows).fill(1);
    this.track();
  }

  /** Mark a device-pixel rect (already transformed) as changed. */
  mark(x0: number, y0: number, x1: number, y1: number) {
    const c0 = Math.max(0, Math.floor(x0 / T) );
    const c1 = Math.min(this.cols - 1, Math.floor(x1 / T));
    const r0 = Math.max(0, Math.floor(y0 / T));
    const r1 = Math.min(this.rows - 1, Math.floor(y1 / T));
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) this.dirty[r * this.cols + c] = 1;
  }

  markAll() {
    this.dirty.fill(1);
  }

  private markRect(x: number, y: number, w: number, h: number) {
    const m = this.g.getTransform();
    if (m.b === 0 && m.c === 0) {
      const ax = m.a * x + m.e;
      const bx = m.a * (x + w) + m.e;
      const ay = m.d * y + m.f;
      const by = m.d * (y + h) + m.f;
      this.mark(Math.min(ax, bx) - 2, Math.min(ay, by) - 2, Math.max(ax, bx) + 2, Math.max(ay, by) + 2);
      return;
    }
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const [px, py] of [[x, y], [x + w, y], [x, y + h], [x + w, y + h]]) {
      const tx = m.a * px + m.c * py + m.e;
      const ty = m.b * px + m.d * py + m.f;
      x0 = Math.min(x0, tx);
      y0 = Math.min(y0, ty);
      x1 = Math.max(x1, tx);
      y1 = Math.max(y1, ty);
    }
    this.mark(x0 - 2, y0 - 2, x1 + 2, y1 + 2);
  }

  /** Wrap the layer's drawing calls so every write marks the tiles it can touch. */
  private track() {
    const g = this.g as unknown as Record<string, (...a: unknown[]) => unknown>;
    const self = this;
    for (const name of ["fillRect", "clearRect", "strokeRect"]) {
      const orig = g[name].bind(this.g);
      g[name] = (x: unknown, y: unknown, w: unknown, h: unknown) => {
        self.markRect(x as number, y as number, w as number, h as number);
        return orig(x, y, w, h);
      };
    }
    const draw = g.drawImage.bind(this.g);
    g.drawImage = (...a: unknown[]) => {
      const img = a[0] as { width: number; height: number };
      if (a.length >= 9) self.markRect(a[5] as number, a[6] as number, a[7] as number, a[8] as number);
      else if (a.length >= 5) self.markRect(a[1] as number, a[2] as number, a[3] as number, a[4] as number);
      else self.markRect(a[1] as number, a[2] as number, img.width, img.height);
      return draw(...a);
    };
    const put = g.putImageData.bind(this.g);
    g.putImageData = (...a: unknown[]) => {
      const img = a[0] as ImageData;
      // putImageData ignores the transform.
      self.mark((a[1] as number) - 1, (a[2] as number) - 1, (a[1] as number) + img.width + 1, (a[2] as number) + img.height + 1);
      return put(...a);
    };
    for (const name of ["fill", "stroke", "fillText", "strokeText"]) {
      const orig = g[name].bind(this.g);
      g[name] = (...a: unknown[]) => {
        self.markAll();
        return orig(...a);
      };
    }
  }

  private tile(i: number) {
    let t = this.tiles[i];
    if (!t) {
      const c = document.createElement("canvas");
      c.width = T;
      c.height = T;
      const g = c.getContext("2d")!;
      t = { c, g };
      this.tiles[i] = t;
      this.dirty[i] = 1;
    }
    if (this.dirty[i]) {
      const col = i % this.cols;
      const row = (i / this.cols) | 0;
      t.g.putImageData(this.g.getImageData(col * T, row * T, T, T), 0, 0);
      this.dirty[i] = 0;
    }
    return t.c;
  }

  /** Same pixels as drawImage(src, sx, sy, w, h, 0, 0, w, h) with smoothing off, from the tiles. */
  drawView(ctx: CanvasRenderingContext2D, sx: number, sy: number, w: number, h: number) {
    // Nearest sampling at a fractional offset picks source pixel round(sx + i): draw at an integer offset.
    const ox = Math.round(sx);
    const oy = Math.round(sy);
    const c0 = Math.max(0, Math.floor(ox / T));
    const c1 = Math.min(this.cols - 1, Math.floor((ox + w) / T));
    const r0 = Math.max(0, Math.floor(oy / T));
    const r1 = Math.min(this.rows - 1, Math.floor((oy + h) / T));
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) ctx.drawImage(this.tile(r * this.cols + c), c * T - ox, r * T - oy);
    }
  }

  /** drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh) without handing the whole layer to the browser. */
  copy(ctx: CanvasRenderingContext2D, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number) {
    if (sw <= 0 || sh <= 0) return;
    const x0 = Math.floor(sx);
    const y0 = Math.floor(sy);
    const w = Math.max(1, Math.ceil(sx + sw) - x0);
    const h = Math.max(1, Math.ceil(sy + sh) - y0);
    if (!this.scratch || this.scratch.width < w || this.scratch.height < h) {
      this.scratch = document.createElement("canvas");
      this.scratch.width = Math.max(w, this.scratch?.width ?? 0);
      this.scratch.height = Math.max(h, this.scratch?.height ?? 0);
      this.sg = this.scratch.getContext("2d", { willReadFrequently: true });
    }
    if (!this.sg) return;
    this.sg.putImageData(this.g.getImageData(x0, y0, w, h), 0, 0);
    ctx.drawImage(this.scratch, sx - x0, sy - y0, sw, sh, dx, dy, dw, dh);
  }
}
